import { useMsal } from '@azure/msal-react'
import {
  faCircleExclamation,
  faShoppingCart,
  faTrash,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Anchor,
  Box,
  Button,
  Divider,
  Grid,
  Group,
  Paper,
  Skeleton,
  Stack,
  Text,
  Title,
  Tooltip,
  UnstyledButton,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import axios from 'axios'
import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import AllLocationsBundleCard from '../components/checkout/AllLocationsBundleCard'
import CartSummary from '../components/checkout/CartSummary'
import ConfirmAdRemovalModalContent from '../components/checkout/ConfirmAdRemovalModalContent'
import { useSessionInfo } from '../contexts/SessionContext'
import {
  createPaymentIntent,
  fetchAdvertisementsInCart,
  fetchAllLocationsBundlesInCart,
  removeAllLocationsBundle,
  removeItemFromCart,
  validateCoupon,
} from '../data/checkout.service'
import { AllLocationsBundle } from '../models/Checkout/AllLocationsBundle'
import { CartDeviceAdvertisement } from '../models/Checkout/CartDeviceAdvertisement'
import { applyCouponDiscount, calculateTotals, getCostForItem } from '../utils/checkoutUtils'
import {
  AdDesignPrice,
  AdSelectionTypes,
  ApprovalTypes,
  COLORS,
} from '../utils/constants'
import { getDateStringFromRange, isBeforeToday } from '../utils/dateUtils'

const CartPage = () => {
  const [cartItems, setCartItems] = useState<CartDeviceAdvertisement[]>([])
  const [bundleItems, setBundleItems] = useState<AllLocationsBundle[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [couponCode, setCouponCode] = useState('')
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null)
  const [couponError, setCouponError] = useState<string | null>(null)
  const [couponSuccess, setCouponSuccess] = useState<string | null>(null)
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false)
  const [isConfirmingFreeOrder, setIsConfirmingFreeOrder] = useState(false)
  const { session, setSession } = useSessionInfo()
  const navigate = useNavigate()
  const { instance } = useMsal()
  const isMobile = useMediaQuery('(max-width: 768px)')

  const handleApplyCoupon = async () => {
    if (!couponCode.trim() || !session?.organizationId) return
    setIsApplyingCoupon(true)
    setCouponError(null)
    setCouponSuccess(null)
    try {
      const result = await validateCoupon(
        couponCode.trim(),
        session.organizationId,
        instance,
      )
      if (!result.valid) {
        setCouponError(result.reason ?? 'Invalid coupon code')
      } else {
        setAppliedCoupon(couponCode.trim())
        setCouponSuccess(result.discountDescription ?? 'Coupon applied!')
      }
    } catch {
      setCouponError('Failed to apply coupon. Please try again.')
    } finally {
      setIsApplyingCoupon(false)
    }
  }

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null)
    setCouponSuccess(null)
    setCouponError(null)
    setCouponCode('')
  }

  const handleProceedToCheckout = async () => {
    if (!session?.organizationId) return

    // Free order — submit directly without going through checkout
    if (discountedTotals.totalAmount === 0 && couponDiscount > 0 && appliedCoupon) {
      setIsConfirmingFreeOrder(true)
      try {
        const result = await createPaymentIntent(
          session.organizationId,
          session.user.id,
          instance,
          appliedCoupon,
        )
        setSession({ ...session, itemsInCart: [], allLocationsBundleCount: 0 })
        navigate('/dashboard/myAds')
        notifications.show({
          title: 'Order confirmed!',
          message: 'Your ads will be up and running shortly.',
          color: 'green',
        })
        if (result?.autoRenewWasDisabled) {
          notifications.show({
            title: 'Auto-Renew Disabled',
            message:
              'Auto-renew has been turned off for your free-month ads. Free orders cannot be renewed automatically — you will need to manually check out again when your campaign ends.',
            color: 'yellow',
            autoClose: false,
          })
        }
      } catch (err) {
        const serverMessage = axios.isAxiosError(err) ? err.response?.data : null
        notifications.show({
          title: 'Something went wrong',
          message: typeof serverMessage === 'string' ? serverMessage : 'Could not place your order. Please try again.',
          color: 'red',
          autoClose: false,
        })
      } finally {
        setIsConfirmingFreeOrder(false)
      }
      return
    }

    navigate('/checkout', {
      state: { couponCode: appliedCoupon ?? undefined },
    })
  }

  // Function to get the display text for ad run length
  const getAdRunLengthText = (item: CartDeviceAdvertisement) => {
    if (item.numberOfDaysToRunAd === null) {
      // Custom date range
      return getDateStringFromRange(
        [item.startDateLocal, item.endDateLocal],
        item.numberOfDaysToRunAd,
      )
    }

    // Predefined options
    switch (item.numberOfDaysToRunAd) {
      case 7:
        return '1 Week'
      case 14:
        return '2 Weeks'
      case 30:
        return '1 Month'
      case 90:
        return '3 Months'
      default:
        return getDateStringFromRange(
          [item.startDateLocal, item.endDateLocal],
          item.numberOfDaysToRunAd,
        )
    }
  }

  const handleRemoveClick = (deviceAdvertisement: CartDeviceAdvertisement) => {
    modals.openConfirmModal({
      title: 'Confirm Removal',
      children: (
        <ConfirmAdRemovalModalContent
          deviceAdvertisement={deviceAdvertisement}
        />
      ),
      labels: { confirm: 'Confirm', cancel: 'Cancel' },
      onConfirm: () => confirmRemoval(deviceAdvertisement.id),
    })
  }

  const confirmRemoval = async (deviceAdvertisementId: number) => {
    try {
      await removeItemFromCart(deviceAdvertisementId, instance)
      if (session) {
        setSession({
          ...session,
          itemsInCart: session?.itemsInCart.filter(
            (i) => i.id !== deviceAdvertisementId,
          ),
        })
      }
      setCartItems((prev) => prev.filter((i) => i.id !== deviceAdvertisementId))
    } catch (ex) {
      notifications.show({
        title: 'An unknown error occurred',
        message:
          'Error removing ad from cart. Try again. If you are unable to resolve the issue, please contact us for help',
        autoClose: false,
      })
    }
  }

  const confirmBundleRemoval = async (advertisementId: number) => {
    try {
      await removeAllLocationsBundle(advertisementId, instance)
      setBundleItems((prev) =>
        prev.filter((b) => b.advertisementId !== advertisementId),
      )
      if (session) {
        setSession({
          ...session,
          allLocationsBundleCount: Math.max(0, (session.allLocationsBundleCount ?? 0) - 1),
        })
      }
    } catch (ex) {
      notifications.show({
        title: 'An unknown error occurred',
        message:
          'Error removing bundle from cart. Try again. If you are unable to resolve the issue, please contact us for help',
        autoClose: false,
      })
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      if (!session?.organizationId) {
        setIsLoading(false)
        return
      }

      try {
        const [cartResponse, bundlesResponse] = await Promise.all([
          fetchAdvertisementsInCart(session.organizationId, instance),
          fetchAllLocationsBundlesInCart(session.organizationId, instance),
        ])

        const itemsWithDates = cartResponse.map((item) => ({
          ...item,
          startDateLocal: item.startDateLocal
            ? new Date(item.startDateLocal)
            : null,
          endDateLocal: item.endDateLocal ? new Date(item.endDateLocal) : null,
        }))

        setCartItems(itemsWithDates)
        setBundleItems(bundlesResponse)
      } catch (error) {
        console.error('Error fetching data:', error)
        notifications.show({
          title: 'Error loading cart',
          message:
            'There was an error loading your cart. Please try refreshing the page.',
          color: 'red',
        })
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [session?.organizationId, instance])

  // Group items by approvalType
  const groupedItems = cartItems.reduce(
    (acc, item) => {
      // Initialize the arrays if they don't exist
      if (!acc[ApprovalTypes.Manual]) {
        acc[ApprovalTypes.Manual] = []
      }
      if (!acc[ApprovalTypes.Auto]) {
        acc[ApprovalTypes.Auto] = []
      }
      acc[item.device.approvalType].push(item)
      return acc
    },
    {
      [ApprovalTypes.Manual]: [],
      [ApprovalTypes.Auto]: [],
    } as Record<ApprovalTypes, CartDeviceAdvertisement[]>,
  )

  const totals = calculateTotals(cartItems)

  const bundleTotal = bundleItems.reduce((sum, b) => sum + b.price, 0)

  const { discountedTotals, couponDiscount, discountedBundleTotal } = applyCouponDiscount(
    totals,
    cartItems,
    bundleTotal,
    !!appliedCoupon,
  )

  // Warn the user before they confirm a free coupon order that had auto-renew items
  const autoRenewWillBeDisabled =
    !!appliedCoupon &&
    discountedTotals.totalAmount === 0 &&
    couponDiscount > 0 &&
    (cartItems.some((item) => item.autoRenew && item.numberOfDaysToRunAd === 30) ||
      bundleItems.some((bundle) => bundle.autoRenew && bundle.numberOfDays === 30))

  const hasPastStartDate = cartItems.some((item) =>
    isBeforeToday(item.startDateLocal),
  )

  const bundleOverlapError = (() => {
    if (bundleItems.length < 2) return null
    const getBundleRange = (b: AllLocationsBundle): [Date, Date] => {
      const start = b.startDateLocal ? new Date(b.startDateLocal) : new Date()
      if (b.endDateLocal) return [start, new Date(b.endDateLocal)]
      if (b.numberOfDays) { const end = new Date(start); end.setDate(end.getDate() + b.numberOfDays); return [start, end] }
      return [start, new Date(8640000000000000)]
    }
    for (let i = 0; i < bundleItems.length; i++) {
      for (let j = i + 1; j < bundleItems.length; j++) {
        const [s1, e1] = getBundleRange(bundleItems[i])
        const [s2, e2] = getBundleRange(bundleItems[j])
        if (s1 < e2 && s2 < e1) return 'Your cart contains overlapping all-locations campaigns. Please remove one before checking out.'
      }
    }
    return null
  })()

  if (isLoading) {
    return (
      <Stack p={isMobile ? 20 : 50} h='100%' gap={isMobile ? 20 : 30}>
        <Title size={isMobile ? 'h2' : 'h1'}>Cart</Title>
        {isMobile ? (
          <Stack gap={20}>
            {[1, 2].map((index) => (
              <Skeleton key={index} height={300} width='100%' radius={20} />
            ))}
          </Stack>
        ) : (
          <Group gap={50} h='100%' w='100%' align='flex-start'>
            <Skeleton flex={8} radius={20} h='60%' />
            <Skeleton flex={3} radius={20} h='50%' />
          </Group>
        )}
      </Stack>
    )
  }

  return (
    <Stack p={isMobile ? 20 : 50} h='100%' gap={isMobile ? 20 : 30}>
      <Title size={isMobile ? 'h2' : 'h1'}>Cart</Title>
      {isMobile ? (
        <Stack gap={20}>
          {(cartItems.length > 0 || bundleItems.length > 0) && (
            <Box w='100%'>
              <CartSummary
                totals={discountedTotals}
                discount={couponDiscount || undefined}
                bundleTotal={discountedBundleTotal || undefined}
                onSubmit={handleProceedToCheckout}
                isButtonDisabled={(cartItems.length === 0 && bundleItems.length === 0) || hasPastStartDate || !!bundleOverlapError || isConfirmingFreeOrder}
                isLoadingSubmit={isConfirmingFreeOrder}
                buttonText={discountedTotals.totalAmount === 0 && couponDiscount > 0 ? 'Confirm Order' : 'Proceed to Checkout'}
                buttonDisabledText={
                  hasPastStartDate
                    ? 'Start Date is in the past for at least 1 advertisement. Please remove this item from the cart.'
                    : bundleOverlapError
                    ?? 'Before proceeding to checkout, you must add at least one advertisement to your cart'
                }
                couponCode={couponCode}
                appliedCoupon={appliedCoupon}
                couponError={couponError}
                couponSuccess={couponSuccess}
                isApplyingCoupon={isApplyingCoupon}
                onCouponCodeChange={setCouponCode}
                onApplyCoupon={handleApplyCoupon}
                onRemoveCoupon={handleRemoveCoupon}
                autoRenewWillBeDisabled={autoRenewWillBeDisabled}
              />
            </Box>
          )}

          <Stack flex={1} w='100%'>
            {bundleItems.map((bundle) => (
              <AllLocationsBundleCard
                key={bundle.advertisementId}
                bundle={bundle}
                onRemove={() => confirmBundleRemoval(bundle.advertisementId)}
              />
            ))}
            {(Object.keys(groupedItems) as ApprovalTypes[]).map(
              (approvalType) =>
                groupedItems[approvalType].length > 0 && (
                  <Paper
                    key={approvalType}
                    withBorder
                    shadow='md'
                    w='100%'
                    radius={20}
                    p={16}
                  >
                    <Stack mb='md'>
                      <Stack gap={0}>
                        <Title w='100%' size='h4'>
                          {approvalType === ApprovalTypes.Manual &&
                            'Ads requiring approval'}
                          {approvalType === ApprovalTypes.Auto &&
                            'Ads that will run automatically'}
                        </Title>

                        {approvalType === ApprovalTypes.Manual && (
                          <Text size='xs' pt={0}>
                            *Your card will be pre-authorized for these ads, but
                            you will not be charged until they are approved.
                          </Text>
                        )}
                      </Stack>
                      <Divider />
                      <Stack gap={20}>
                        {groupedItems[approvalType].map(
                          (item: CartDeviceAdvertisement) => (
                            <React.Fragment key={item.id}>
                              <Stack gap={10}>
                                <Group justify='space-between' wrap='nowrap'>
                                  <Anchor
                                    component={Link}
                                    to={`/locations/${item.device.locationId}`}
                                  >
                                    <Title size='h4'>{`${item.locationName} - ${item.device.name}`}</Title>
                                  </Anchor>
                                  <UnstyledButton
                                    onClick={() => handleRemoveClick(item)}
                                  >
                                    <FontAwesomeIcon
                                      icon={faTrash}
                                      color='gray'
                                    />
                                  </UnstyledButton>
                                </Group>
                                <Text size='sm' fw={500}>
                                  {item.advertisement.name}
                                </Text>
                                {isBeforeToday(item.startDateLocal) ? (
                                  <Tooltip
                                    label='Start Date is in the past. Please remove this item from the cart.'
                                    color='red'
                                  >
                                    <Group align='center'>
                                      <FontAwesomeIcon
                                        icon={faCircleExclamation}
                                        color='red'
                                      />
                                      <Text c='red' size='sm'>
                                        {getAdRunLengthText(item)}
                                        {item.autoRenew && ' (Auto-Renew)'}
                                      </Text>
                                    </Group>
                                  </Tooltip>
                                ) : (
                                  <Text size='sm'>
                                    {getAdRunLengthText(item)}
                                    {item.autoRenew && ' (Auto-Renew)'}
                                  </Text>
                                )}
                                <Group justify='space-between'>
                                  <Text fw={700}>
                                    ${getCostForItem(item).toFixed(2)}
                                  </Text>
                                  {item.advertisement.adSelectionType ===
                                    AdSelectionTypes.PayForDesign && (
                                    <Text size='sm'>
                                      + ${AdDesignPrice} Ad Design
                                    </Text>
                                  )}
                                </Group>
                                <Divider />
                              </Stack>
                            </React.Fragment>
                          ),
                        )}
                      </Stack>
                    </Stack>
                  </Paper>
                ),
            )}
            {cartItems.length === 0 && bundleItems.length === 0 && (
              <Paper withBorder shadow='md' radius={20} p={16}>
                <Stack align='center' p={30} gap={20}>
                  <FontAwesomeIcon
                    icon={faShoppingCart}
                    color={COLORS.BasicTextGray}
                    style={{ height: 80 }}
                  />
                  <Title size='h3'>Your cart is currently empty</Title>
                  <Text c={COLORS.BasicTextGray} size='sm' ta='center'>
                    Before proceeding to checkout, you must add at least one
                    advertisement to your cart
                  </Text>
                  <Button onClick={() => navigate('/')}>
                    Continue Shopping
                  </Button>
                </Stack>
              </Paper>
            )}
          </Stack>

          {(cartItems.length > 0 || bundleItems.length > 0) && (
            <Button
              fullWidth
              size='md'
              radius={12}
              onClick={handleProceedToCheckout}
              disabled={(cartItems.length === 0 && bundleItems.length === 0) || hasPastStartDate || !!bundleOverlapError || isConfirmingFreeOrder}
              loading={isConfirmingFreeOrder}
              mt={20}
            >
              {discountedTotals.totalAmount === 0 && couponDiscount > 0
                ? 'Confirm Order'
                : 'Proceed to Checkout'}
            </Button>
          )}
        </Stack>
      ) : (
        <Group gap={50} align='flex-start'>
          <Stack flex={8} w='auto'>
            {bundleItems.map((bundle) => (
              <AllLocationsBundleCard
                key={bundle.advertisementId}
                bundle={bundle}
                onRemove={() => confirmBundleRemoval(bundle.advertisementId)}
              />
            ))}
            {(Object.keys(groupedItems) as ApprovalTypes[]).map(
              (approvalType) =>
                groupedItems[approvalType].length > 0 && (
                  <Paper
                    key={approvalType}
                    withBorder
                    shadow='md'
                    w='100%'
                    radius={20}
                    p={32}
                  >
                    <Stack mb='md'>
                      <Stack gap={0}>
                        <Title w={700} size='h3'>
                          {approvalType === ApprovalTypes.Manual &&
                            'Ads requiring approval'}
                          {approvalType === ApprovalTypes.Auto &&
                            'Ads that will run automatically'}
                        </Title>

                        {approvalType === ApprovalTypes.Manual && (
                          <Text size='sm' pt={0}>
                            *Your card will be pre-authorized for these ads, but
                            you will not be charged until they are approved.
                          </Text>
                        )}
                      </Stack>
                      <Grid m={24}>
                        <Grid.Col span={4} fw={700} pb={0}>
                          Location
                        </Grid.Col>
                        <Grid.Col span={3} fw={700} pb={0}>
                          Ad Name
                        </Grid.Col>
                        <Grid.Col span={3} fw={700} pb={0}>
                          Ad Run Length
                        </Grid.Col>
                        <Grid.Col span={2} fw={700} pb={0}>
                          Total
                        </Grid.Col>
                      </Grid>
                      <Divider px={24} />
                      <Stack gap={0}>
                        {groupedItems[approvalType].map(
                          (item: CartDeviceAdvertisement) => (
                            <React.Fragment key={item.id}>
                              <Grid m={24}>
                                <Grid.Col span={4}>
                                  <Stack justify='center' h='100%'>
                                    <Anchor
                                      component={Link}
                                      to={`/locations/${item.device.locationId}`}
                                    >
                                      <Title size='h4'>{`${item.locationName} - ${item.device.name}`}</Title>
                                    </Anchor>
                                  </Stack>
                                </Grid.Col>
                                <Grid.Col span={3}>
                                  <Stack justify='center' h='100%'>
                                    <Text>{item.advertisement.name}</Text>
                                  </Stack>
                                </Grid.Col>
                                <Grid.Col span={3}>
                                  {isBeforeToday(item.startDateLocal) ? (
                                    <Tooltip
                                      label='Start Date is in the past. Please remove this item from the cart.'
                                      color='red'
                                    >
                                      <Group align='center' h='100%'>
                                        <FontAwesomeIcon
                                          icon={faCircleExclamation}
                                          color='red'
                                        />
                                        <Text c='red'>
                                          {getAdRunLengthText(item)}
                                          {item.autoRenew && ' (Auto-Renew)'}
                                        </Text>
                                      </Group>
                                    </Tooltip>
                                  ) : (
                                    <Group align='center' h='100%'>
                                      <Text>
                                        {getAdRunLengthText(item)}
                                        {item.autoRenew && ' (Auto-Renew)'}
                                      </Text>
                                    </Group>
                                  )}
                                </Grid.Col>
                                <Grid.Col span={2}>
                                  <Group
                                    align='center'
                                    h='100%'
                                    wrap='nowrap'
                                    justify='space-between'
                                  >
                                    <Text fw={700}>
                                      ${getCostForItem(item).toFixed(2)}
                                    </Text>
                                    <UnstyledButton
                                      h='100%'
                                      style={{ justifyContent: 'center' }}
                                      onClick={() => handleRemoveClick(item)}
                                    >
                                      <FontAwesomeIcon
                                        icon={faTrash}
                                        color='gray'
                                      />
                                    </UnstyledButton>
                                  </Group>
                                </Grid.Col>
                                {item.advertisement.adSelectionType ===
                                  AdSelectionTypes.PayForDesign && (
                                  <>
                                    <Grid.Col span={3} offset={7}>
                                      <Stack justify='center' h='100%'>
                                        <Text size='sm'>
                                          + ${AdDesignPrice} Ad Design
                                        </Text>
                                      </Stack>
                                    </Grid.Col>
                                    <Grid.Col span={2}>
                                      <Group
                                        align='center'
                                        h='100%'
                                        wrap='nowrap'
                                        justify='space-between'
                                      >
                                        <Text fw={700}>${AdDesignPrice}</Text>
                                      </Group>
                                    </Grid.Col>
                                  </>
                                )}
                                <Grid.Col span={12}>
                                  <Divider />
                                </Grid.Col>
                              </Grid>
                            </React.Fragment>
                          ),
                        )}
                      </Stack>
                    </Stack>
                  </Paper>
                ),
            )}
            {cartItems.length === 0 && bundleItems.length === 0 && (
              <Paper withBorder shadow='md' radius={20} p={32}>
                <Stack align='center' p={50} gap={40}>
                  <FontAwesomeIcon
                    icon={faShoppingCart}
                    color={COLORS.BasicTextGray}
                    style={{ height: 128 }}
                  />
                  <Title size='h2'>Your cart is currently empty</Title>
                  <Text c={COLORS.BasicTextGray} size='md' ta='center'>
                    Before proceeding to checkout, you must add at least one
                    advertisement to your cart
                  </Text>
                  <Button onClick={() => navigate('/')}>
                    Continue Shopping
                  </Button>
                </Stack>
              </Paper>
            )}
          </Stack>

          <Box flex={3}>
            <CartSummary
              totals={discountedTotals}
              discount={couponDiscount || undefined}
              bundleTotal={discountedBundleTotal || undefined}
              onSubmit={handleProceedToCheckout}
              isButtonDisabled={(cartItems.length === 0 && bundleItems.length === 0) || hasPastStartDate || isConfirmingFreeOrder}
              isLoadingSubmit={isConfirmingFreeOrder}
              buttonText={discountedTotals.totalAmount === 0 && couponDiscount > 0 ? 'Confirm Order' : 'Proceed to Checkout'}
              buttonDisabledText={
                hasPastStartDate
                  ? 'Start Date is in the past for at least 1 advertisement. Please remove this item from the cart.'
                  : 'Before proceeding to checkout, you must add at least one advertisement to your cart'
              }
              couponCode={couponCode}
              appliedCoupon={appliedCoupon}
              couponError={couponError}
              couponSuccess={couponSuccess}
              isApplyingCoupon={isApplyingCoupon}
              onCouponCodeChange={setCouponCode}
              onApplyCoupon={handleApplyCoupon}
              onRemoveCoupon={handleRemoveCoupon}
              autoRenewWillBeDisabled={autoRenewWillBeDisabled}
            />
          </Box>
        </Group>
      )}
    </Stack>
  )
}

export default CartPage
